import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiCalendar, FiClock } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const BookingSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add'); // add, edit, delete
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [formData, setFormData] = useState({
    booking_date: '',
    booking_time: '',
    status: 'pending'
  });

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    'in-progress': 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const statusLabels = {
    pending: 'Menunggu',
    confirmed: 'Dikonfirmasi',
    'in-progress': 'Sedang Dikerjakan',
    completed: 'Selesai',
    cancelled: 'Dibatalkan',
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the new schedule endpoint
      console.log('Fetching booking schedules from /api/schedule');
      const response = await API.get('/api/schedule');
      console.log('Fetched schedules:', response.data);
      setSchedules(response.data);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      setError('Gagal memuat jadwal booking. Silakan coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleOpenModal = (type, schedule = null) => {
    setModalType(type);
    setSelectedSchedule(schedule);

    if (type === 'add') {
      setFormData({
        booking_date: '',
        booking_time: '',
        status: 'pending'
      });
    } else if (type === 'edit' && schedule) {
      setFormData({
        booking_date: schedule.booking_date,
        booking_time: schedule.booking_time,
        status: schedule.status
      });
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalType === 'add') {
        await API.post('/api/schedule', formData);
      } else if (modalType === 'edit') {
        await API.put(`/api/schedule/${selectedSchedule.id}`, formData);
      }

      fetchSchedules();
      closeModal();
    } catch (err) {
      console.error(`Error ${modalType === 'add' ? 'adding' : 'updating'} schedule:`, err);
      setError(`Gagal ${modalType === 'add' ? 'menambahkan' : 'memperbarui'} jadwal booking.`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;

    setLoading(true);
    try {
      await API.delete(`/api/schedule/${selectedSchedule.id}`);
      fetchSchedules();
      closeModal();
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Gagal menghapus jadwal booking.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderAddEditModal = () => {
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isModalOpen ? '' : 'hidden'}`}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">
              {modalType === 'add' ? 'Tambah Jadwal Booking' : 'Edit Jadwal Booking'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="booking_date">
                  Tanggal Booking
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FiCalendar className="text-gray-500" />
                  </div>
                  <input
                    type="date"
                    id="booking_date"
                    name="booking_date"
                    value={formData.booking_date}
                    onChange={handleInputChange}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="booking_time">
                  Waktu Booking
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FiClock className="text-gray-500" />
                  </div>
                  <input
                    type="time"
                    id="booking_time"
                    name="booking_time"
                    value={formData.booking_time}
                    onChange={handleInputChange}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  required
                >
                  <option value="pending">Menunggu</option>
                  <option value="confirmed">Dikonfirmasi</option>
                  <option value="in-progress">Sedang Dikerjakan</option>
                  <option value="completed">Selesai</option>
                  <option value="cancelled">Dibatalkan</option>
                </select>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={loading}
                >
                  {loading ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isModalOpen && modalType === 'delete' ? '' : 'hidden'}`}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Hapus Jadwal Booking</h2>
            <p className="mb-6">
              Apakah Anda yakin ingin menghapus jadwal booking ini?
              <br />
              <span className="font-semibold">Tanggal: {selectedSchedule ? formatDate(selectedSchedule.booking_date) : ''}</span>
              <br />
              <span className="font-semibold">Waktu: {selectedSchedule ? selectedSchedule.booking_time : ''}</span>
            </p>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={loading}
              >
                {loading ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6 bg-orange-500 text-white flex justify-between items-center">
          <h2 className="text-2xl font-bold">Jadwal Booking</h2>
          <button
            onClick={() => handleOpenModal('add')}
            className="bg-white text-orange-500 px-4 py-2 rounded-md flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <FiPlus /> Tambah Jadwal
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
            <p>{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  No
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tanggal
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Waktu
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    Memuat data...
                  </td>
                </tr>
              ) : schedules.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    Belum ada jadwal booking
                  </td>
                </tr>
              ) : (
                schedules.map((schedule, index) => (
                  <tr key={schedule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(schedule.booking_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {schedule.booking_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[schedule.status]}`}>
                        {statusLabels[schedule.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenModal('edit', schedule)}
                          className="text-yellow-500 hover:text-yellow-700"
                        >
                          <FiEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenModal('delete', schedule)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalType !== 'delete' ? renderAddEditModal() : renderDeleteModal()}
    </DashboardLayout>
  );
};

export default BookingSchedule; 